import * as _ from "lodash";
import { IdsAttribute, IdsFilterSpec, IdsMetric, IdsNode, IdsRow, IMetricDataSetProvider } from "./PA.MetricDataSet.IF";
import { IMetricDataProviderOptions } from "./IMetricDataProviderOptions";
import { FilterUtils } from "../PA.Core/Basic/Filter";
import { HierarchyPathBuilder } from "../PA.MD.Hierarchy/HierarchyPathBuilder";

interface IPathAttribute extends IdsAttribute {
    columnName: string;
}

export interface IHierarchyPathDataProviderOptions extends IMetricDataProviderOptions {
    /** "Customer", "Product", etc. - a plain string rather than the DimensionType enum, since this
     *  provider is constructed from UI5 AMD control files where a value-level enum import isn't safe. */
    entityDimension: string;
    /** The dimension-specific hierarchy id (WB_CUSTH_ID, WB_PRDH_ID, etc.), not the generic WB_HIERARCHY_ID. */
    wbTargetId: number | string;
    /** SAP date string, YYYYMMDD */
    effectiveDt: string;
}

/**
 * Feeds the Product/Customer Hierarchy reports from HierarchyPathBuilder, which flattens the same
 * HierarchyStructureNavigator tree (real parent pointers, unbounded depth, actual level names) that
 * PA-HierarchyEditor's Hierarchy Tree renders - rather than a SQL tree-walk that can't replicate that
 * graph's exact structure or level naming.
 */
export class HierarchyPathDataProvider implements IMetricDataSetProvider {
    private _createOptions: IHierarchyPathDataProviderOptions;
    private _name: string;
    private _dctAttribute: { [attributeId: string]: IPathAttribute } = {};
    private dctColumnName: { [attributeId: string]: string } = {};
    private _bLoaded = false;
    private _onLoadComplete: Promise<void>;
    private _rows: IdsRow[] = [];
    private _actualLevelCount: number;

    /** The level count actually populated by the real data (available once OnLoadComplete resolves) -
     *  may differ from the upfront estimate used to build this provider's attributes, e.g. when a
     *  configured level exists but no leaf's chain ever reaches it. Callers compare this against the
     *  estimate they used and rebuild with the corrected count if they differ. */
    get ActualLevelCount(): number {
        return this._actualLevelCount;
    }

    constructor(createOptions: IHierarchyPathDataProviderOptions) {
        this._createOptions = createOptions;
        this._name = createOptions?.name || "Hierarchy Path Data Provider";

        _.forEach(createOptions.attributes, (attr) => {
            this._dctAttribute[attr.id] = {
                Id: attr.id,
                Name: attr.name,
                ShortName: attr.shortName || attr.name,
                Type: attr.type,
                SortOrder: attr.sort || 9999,
                columnName: attr.columnName
            };
            this.dctColumnName[attr.id] = attr.columnName;
        });
    }

    get bLoaded(): boolean {
        return this._bLoaded;
    }

    get Name(): string {
        return this._name;
    }

    OnLoadComplete(): Promise<void> {
        if (this._onLoadComplete) return this._onLoadComplete;

        this._onLoadComplete = (async () => {
            const effDate = HierarchyPathDataProvider.parseSapDate(this._createOptions.effectiveDt);
            const results = await HierarchyPathBuilder.getHierarchyPaths(
                this._createOptions.entityDimension,
                Number(this._createOptions.wbTargetId),
                effDate,
                effDate
            );
            this._rows = results?.rows || [];
            this._actualLevelCount = results?.levelCount;
            this._bLoaded = true;
        })();

        return this._onLoadComplete;
    }

    private static parseSapDate(sapDate: string): Date {
        const y = +sapDate.substring(0, 4);
        const m = +sapDate.substring(4, 6) - 1;
        const d = +sapDate.substring(6, 8);
        return new Date(y, m, d);
    }

    getAttributes(): IdsAttribute[] {
        return _.values(this._dctAttribute);
    }

    getMetrics(): IdsMetric[] {
        return [];
    }

    async getDataRows(
        arrAttributeIds: string[],
        arrMetricIds: string[],
        filters: IdsFilterSpec[],
        rowCountThreshold?: number,
        bCountOnly?: boolean
    ): Promise<IdsRow[]> {
        await this.OnLoadComplete();
        const filteredRows = this.distinctByAttributes(this.applyFilters(this._rows, filters), arrAttributeIds);

        if (bCountOnly) return [<any>{ CNT: filteredRows.length }];
        if (rowCountThreshold) return filteredRows.slice(0, rowCountThreshold);
        return filteredRows;
    }

    async getDataSetRowCount(arrAttributeIds: string[], filters: IdsFilterSpec[]): Promise<number> {
        await this.OnLoadComplete();
        return this.distinctByAttributes(this.applyFilters(this._rows, filters), arrAttributeIds).length;
    }

    // Each leaf customer/product produces its own row, so a narrower column selection (e.g. just L1)
    // would otherwise still show one row per leaf. DBMetricDataSetProvider collapses this the same way
    // via "SELECT DISTINCT <selected columns>" - mirror that here so only the selected attributes drive
    // row uniqueness, matching what the report's column picker leads the user to expect.
    private distinctByAttributes(rows: IdsRow[], arrAttributeIds: string[]): IdsRow[] {
        if (_.isEmpty(arrAttributeIds)) return rows;

        const colNames = _.filter(_.map(arrAttributeIds, (id) => this.dctColumnName[id]), (c) => !_.isEmpty(c));
        if (_.isEmpty(colNames)) return rows;

        return _.uniqBy(rows, (r) => _.map(colNames, (c) => (<any>r)[c]).join("\u0001"));
    }

    private applyFilters(rows: IdsRow[], filters: IdsFilterSpec[]): IdsRow[] {
        if (_.isEmpty(filters)) return rows;

        const self = this;
        function symbolGet(symbolId: string, row: IdsRow): any {
            return self.getRowAttribute(row, symbolId);
        }

        const dctSimpleType: { [id: string]: "string" } = {};
        _.forEach(this._dctAttribute, (a, id) => {
            dctSimpleType[id] = "string";
        });

        const cndFunctions = _.filter(
            _.map(filters, (flt) => FilterUtils.createFunction(flt.condition, dctSimpleType, symbolGet)),
            (fn) => _.isFunction(fn)
        );

        return _.filter(rows, (row) => _.every(cndFunctions, (fn) => fn(row)));
    }

    getRowAttribute(row: IdsRow, attributeId: string): any {
        const colName = this.dctColumnName[attributeId];
        return (<any>row || {})[colName];
    }

    getRowMetric(row: IdsRow, metricId: string): number {
        return undefined;
    }

    setRowData(row: IdsRow, keyId: string, value: any): void {
        if (_.isEmpty(row)) return;
        const anyRow = <any>row;
        anyRow.keyStore = anyRow.keyStore || {};
        anyRow.keyStore[keyId] = value;
    }

    getRowData(row: IdsRow, keyId: string): any {
        return (<any>(row || {}))?.keyStore?.[keyId];
    }

    getRowAggregateMetric(rows: IdsRow[], metricId: string): number {
        return undefined;
    }

    async getDistinctValues(attributeId: string, filters?: IdsFilterSpec[]): Promise<any[]> {
        await this.OnLoadComplete();
        const colName = this.dctColumnName[attributeId];
        if (_.isEmpty(colName)) return [];

        const filteredRows = this.applyFilters(this._rows, filters || []);
        const values = _.map(filteredRows, (r) => (<any>r)[colName]);
        return _.uniq(_.filter(values, (v) => !_.isUndefined(v) && !_.isNull(v)));
    }

    async getValueRange(attributeId: string, filters?: IdsFilterSpec[]): Promise<{ min: any; max: any }> {
        return { min: undefined, max: undefined };
    }

    async getAttributeNodes(attributeId: string, ids: number[]): Promise<IdsNode[]> {
        return [];
    }
}
